import React, { useEffect, useMemo } from "react";
import { Link } from "gatsby";
import { useLocation } from "@reach/router";
import { Layout } from "~/templates/Layout";
import { FormNotice } from "~/molecules/FormNotice";
import { colors } from "~/utils/colors";
import { buildHubSpotMeetingEmbedUrl, CONTACT_FALLBACK, PATH } from "~/constant/path";

const MeetingBookingPage: React.FC = () => {
  const location = useLocation();
  const meetingEmbedUrl = useMemo(
    () => buildHubSpotMeetingEmbedUrl(location.search),
    [location.search],
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [meetingEmbedUrl]);

  return (
    <>
      <style jsx>{`
        .wrapper {
          background: ${colors.bgGrayGradient};
          min-height: 80vh;
          padding: 40px 0 80px;
        }
        .container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .title {
          font-size: 32px;
          font-weight: bold;
          text-align: center;
          margin-bottom: 12px;
          color: ${colors.defaultText};
        }
        .description {
          font-size: 16px;
          line-height: 1.8;
          text-align: center;
          margin-bottom: 24px;
          color: ${colors.defaultText};
        }
        .hubspot {
          display: flex;
          justify-content: center;
        }
        .meetings-iframe-container {
          width: 100%;
          /* CLS 改善: iframe 読み込み前のスペースを確保 */
          min-height: 690px;
        }
        @media (max-width: 768px) {
          .wrapper {
            padding: 24px 0 60px;
          }
          .title {
            font-size: 24px;
          }
          .description {
            font-size: 15px;
            margin-bottom: 20px;
          }
        }
      `}</style>
      <style jsx global>{`
        .meetingAvailabilityNotice {
          max-width: 700px;
          margin: 0 auto 40px;
          padding: 16px 20px;
          background: ${colors.lightOrange};
          border: 1px solid ${colors.subOrange};
          border-radius: 8px;
          font-size: 14px;
          line-height: 1.7;
          color: ${colors.defaultText};
          text-align: center;
        }
        a.meetingAvailabilityNoticeLink,
        a.meetingAvailabilityNoticeLink:link,
        a.meetingAvailabilityNoticeLink:visited,
        a.meetingAvailabilityNoticeLink:hover,
        a.meetingAvailabilityNoticeLink:active {
          color: ${colors.brand} !important;
          text-decoration: underline !important;
        }
        a.meetingAvailabilityNoticeLink:hover {
          opacity: 0.8;
        }
        @media (max-width: 768px) {
          .meetingAvailabilityNotice {
            margin-bottom: 28px;
            padding: 14px 16px;
            font-size: 13px;
          }
        }
      `}</style>
      <Layout
        title="オンライン相談のご予約"
        ogpDescription="Refcomeのオンライン相談をご予約いただけます。日程調整からご希望の日時をお選びください。"
        breadCrumbItems={[PATH.TOP, PATH.MEETING_BOOKING]}
      >
        <div className="wrapper">
          <div className="container">
            <h1 className="title">オンライン相談のご予約</h1>
            <p className="description">
              ご希望の日時を選んで、オンライン相談をご予約ください。
              <br />
              採用のお悩みや導入のご相談など、お気軽にお話しください。
            </p>
            <p className="meetingAvailabilityNotice">
              空き枠以外でも調整できる場合がございます。
              <br />
              ご希望の日時がないときは、お電話（
              <a className="meetingAvailabilityNoticeLink" href={`tel:${CONTACT_FALLBACK.tel.replace(/-/g, "")}`}>
                {CONTACT_FALLBACK.tel}
              </a>
              ）または
              <Link className="meetingAvailabilityNoticeLink" to={PATH.INQUIRY_CONTACT.to}>
                お問い合わせフォーム
              </Link>
              へご連絡ください。
            </p>
            <div className="hubspot">
              <div className="meetings-iframe-container" data-src={meetingEmbedUrl} />
            </div>
            <FormNotice />
          </div>
        </div>
      </Layout>
    </>
  );
};

export default MeetingBookingPage;
